import React from 'react';
import { Box, Typography, Card, CardContent, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, LinearProgress, Chip, Alert, Collapse, IconButton, Divider } from '@mui/material';
import { TrendingUp as TrendingUpIcon, Assessment as AssessmentIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, UnfoldMore as UnfoldMoreIcon, Visibility as VisibilityIcon, AttachMoney as AttachMoneyIcon, ExpandMore, ExpandLess, KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon, Science as ScienceIcon, PlayArrow as PlayArrowIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Category as CategoryIcon, Group as GroupIcon } from '@mui/icons-material';
import { tableStyles, nestedTableStyles } from '../reportingStyles';
import { useReporting } from '../ReportingContext';
import BlogFilters from './BlogFilters';

const SnakeStatsTab = () => {
  const {
    blogStats,
    blogStatsLoading,
    blogStatsError,
    expandedCCRows,
    affiliates,
    setExpandedCCRows,
    snakeSortColumn,
    snakeSortDirection,
    snakeAdminRevExpanded,
    setSnakeAdminRevExpanded,
    handleSnakeSort,
    renderSortIcon,
  } = useReporting();

  let groupedCCStats = [];
if (blogStats?.snake?.contentCreatorDetailedBlogs) {
  const grouped = {};
  blogStats.snake.contentCreatorDetailedBlogs.forEach(blog => {
    const authorId = blog.authorId || 'Unknown';
    if (!grouped[authorId]) {
      grouped[authorId] = {
        authorId: authorId,
        authorName: blog.authorName || authorId,
        authorUsername: blog.authorUsername || '',
        views: 0,
        videoPlays: 0,
        clicks: 0,
        sales: 0,
        revenue: 0,
        blogsArray: []
      };
    }
    
    grouped[authorId].views += blog.views || 0;
    grouped[authorId].videoPlays += blog.videoPlays || 0;
    grouped[authorId].clicks += blog.clicks || 0;
    grouped[authorId].sales += blog.sales || 0;
    grouped[authorId].revenue += blog.revenue || 0;
    
    grouped[authorId].blogsArray.push(blog);
  });
  
  groupedCCStats = Object.values(grouped).map(aff => {
    aff.blogsArray.sort((a, b) => b.revenue - a.revenue);
    return aff;
  }).sort((a, b) => b.revenue - a.revenue);
}

  return (
    <Box>
  {/* Filters Section */}
  <BlogFilters />

  {blogStatsLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress />
    </Box>
  ) : blogStatsError ? (
    <Typography color="error" sx={{ mb: 2 }}>{blogStatsError}</Typography>
  ) : blogStats?.snake ? (
    <>
      {/* Content Creator Earnings Section */}
      {blogStats.snake?.contentCreatorDetailedBlogs && blogStats.snake.contentCreatorDetailedBlogs.length > 0 ? (
        <Card sx={{ mb: 4, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ p: 2, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
                Content Creator Earnings (Others using your content)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Revenue earned when your blogs were used as the "snake" portion of OTHER affiliates' traffic.
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 48 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Content Creator</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '10%' }}>Views</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '10%' }}>Video Plays</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '10%' }}>Clicks</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '10%' }}>CTR</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '10%' }}>Snake Sales</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '10%' }}>CVR</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '10%' }}>RPV</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: '12%' }}>CC Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedCCStats.map((affGroup) => {
                    const isExpanded = expandedCCRows[affGroup.authorId];
                    const groupCtr = affGroup.views > 0 ? ((affGroup.clicks / affGroup.views) * 100).toFixed(2) : '0.00';
                    const groupCvr = affGroup.clicks > 0 ? ((affGroup.sales / affGroup.clicks) * 100).toFixed(2) : '0.00';
                    const groupRpv = affGroup.views > 0 ? (affGroup.revenue / affGroup.views).toFixed(2) : '0.00';

                    return (
                      <React.Fragment key={affGroup.authorId}>
                        {/* Top-level Affiliate Row */}
                        <TableRow
                          hover
                          onClick={() => setExpandedCCRows(prev => ({ ...prev, [affGroup.authorId]: !prev[affGroup.authorId] }))}
                          sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
                        >
                          <TableCell padding="checkbox">
                            <IconButton size="small">
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {affGroup.authorName} {affGroup.authorUsername ? `(@${affGroup.authorUsername})` : ''}
                          </TableCell>
                          <TableCell align="center">{affGroup.views}</TableCell>
                          <TableCell align="center">{affGroup.videoPlays}</TableCell>
                          <TableCell align="center">{affGroup.clicks}</TableCell>
                          <TableCell align="center">{groupCtr}%</TableCell>
                          <TableCell align="center">{affGroup.sales}</TableCell>
                          <TableCell align="center">{groupCvr}%</TableCell>
                          <TableCell align="center">${groupRpv}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#059669' }}>
                            ${affGroup.revenue.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        {/* Expanded Blogs/Offers Details */}
                        {isExpanded && (
                          <TableRow>
                            <TableCell sx={{ pb: 0, pt: 0, px: 0, border: 'none' }} colSpan={4}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 2, pl: 8, pr: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
                                    Converted Blogs & Offers:
                                  </Typography>
                                  <Table size="small" sx={{ mb: 2, border: '1px solid #e5e7eb' }}>
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: '#f9fafb' }}>
                                        <TableCell sx={{ width: 220, fontWeight: 600 }}>Blog</TableCell>
                                        <TableCell sx={{ width: 180, fontWeight: 600 }}>Traffic Provider</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Offer & Element</TableCell>
                                        <TableCell align="center" sx={{ width: 80, fontWeight: 600 }}>Views</TableCell>
                                        <TableCell align="center" sx={{ width: 80, fontWeight: 600 }}>Video Plays</TableCell>
                                        <TableCell align="center" sx={{ width: 80, fontWeight: 600 }}>Clicks</TableCell>
                                        <TableCell align="center" sx={{ width: 80, fontWeight: 600 }}>CTR</TableCell>
                                        <TableCell align="center" sx={{ width: 80, fontWeight: 600 }}>Sales</TableCell>
                                        <TableCell align="center" sx={{ width: 80, fontWeight: 600 }}>CVR</TableCell>
                                        <TableCell align="center" sx={{ width: 100, fontWeight: 600 }}>RPV</TableCell>
                                        <TableCell align="center" sx={{ width: 100, fontWeight: 600 }}>Revenue</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {affGroup.blogsArray.map(blog => {
                                        const blogViews = blog.views || 0;
                                        const blogCtr = blogViews > 0 ? ((blog.clicks || 0) / blogViews * 100).toFixed(2) : '0.00';
                                        const blogCvr = blogViews > 0 ? ((blog.sales || 0) / blogViews * 100).toFixed(2) : '0.00';
                                        const blogRpv = blogViews > 0 ? ((blog.revenue || 0) / blogViews).toFixed(2) : '0.00';

                                        return (
                                          <React.Fragment key={`${blog.blogPostId}-${blog.affiliateId}`}>
                                            {/* Blog Row */}
                                            <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                                              <TableCell sx={{ fontWeight: 600 }}>
                                                {/^Blog \S+$/.test(blog.blogTitle) ? '—' : blog.blogTitle}
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600 }}>
                                                {blog.affiliateName} {blog.affiliateUsername ? `(@${blog.affiliateUsername})` : ''}
                                              </TableCell>
                                              <TableCell></TableCell>
                                              <TableCell align="center" sx={{ fontWeight: 600 }}>{blogViews}</TableCell>
                                              <TableCell align="center" sx={{ fontWeight: 600 }}>{blog.videoPlays || 0}</TableCell>
                                              <TableCell align="center" sx={{ fontWeight: 600 }}>{blog.clicks || 0}</TableCell>
                                              <TableCell align="center" sx={{ fontWeight: 600 }}>{blogCtr}%</TableCell>
                                              <TableCell align="center" sx={{ fontWeight: 600 }}>{blog.sales || 0}</TableCell>
                                              <TableCell align="center" sx={{ fontWeight: 600 }}>{blogCvr}%</TableCell>
                                              <TableCell align="center" sx={{ fontWeight: 600 }}>${blogRpv}</TableCell>
                                              <TableCell align="center" sx={{ fontWeight: 600, color: '#059669' }}>${(blog.revenue || 0).toFixed(2)}</TableCell>
                                            </TableRow>
                                            {/* Offers within Blog */}
                                            {(blog.offers || []).map((offer, idx) => {
                                              const offerCtr = blogViews > 0 ? ((offer.clicks || 0) / blogViews * 100).toFixed(2) : '0.00';
                                              const offerCvr = blogViews > 0 ? ((offer.sales || 0) / blogViews * 100).toFixed(2) : '0.00';
                                              const offerRpv = blogViews > 0 ? ((offer.revenue || 0) / blogViews).toFixed(2) : '0.00';

                                              return (
                                                <TableRow key={idx}>
                                                  <TableCell></TableCell>
                                                  <TableCell></TableCell>
                                                  <TableCell>
                                                    <Typography variant="body2">{offer.offerName}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{offer.offerElementName}</Typography>
                                                  </TableCell>
                                                  <TableCell align="center">—</TableCell>
                                                  <TableCell align="center">—</TableCell>
                                                  <TableCell align="center">{offer.clicks || 0}</TableCell>
                                                  <TableCell align="center">{offerCtr}%</TableCell>
                                                  <TableCell align="center">{offer.sales || 0}</TableCell>
                                                  <TableCell align="center">{offerCvr}%</TableCell>
                                                  <TableCell align="center">${offerRpv}</TableCell>
                                                  <TableCell align="center" sx={{ color: '#059669' }}>${(offer.revenue || 0).toFixed(2)}</TableCell>
                                                </TableRow>
                                              );
                                            })}
                                          </React.Fragment>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* Total Row */}
                  <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                    <TableCell padding="checkbox" sx={{ width: 48 }} />
                    <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      {blogStats.snake.contentCreator?.sales || 0}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#059669' }}>
                      ${(blogStats.snake.contentCreator?.revenue || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No content creator earnings found for this period. CC earnings appear when your blog content is used as snake by other affiliates.
        </Typography>
      )}
    </>
  ) : (
    <Typography variant="body2" color="text.secondary">
      No snake stats available. Snake stats appear when blogs have merged content from other affiliates.
    </Typography>
  )}
</Box>
  );
};

export default SnakeStatsTab;
